// Chemin: C:\gestion-copro\src\pages\vigie\FicheLocataireVigiePage.tsx
// Session 268 : Création — fiche locataire dédiée avec génération quittance PDF (jsPDF)
// Session 269 : Refonte workflow — appel de loyer → quittance (2 étapes comme Rentila)
//   - Type appel_loyer : PDF "Avis d'échéance", envoyable par mail, statut en_attente
//   - Type quittance_loyer : généré depuis l'appel, confirme le paiement reçu
//   - Section GED : 2 groupes séparés — Appels en attente / Quittances émises
//   - locataireId stocké sur les deux types pour filtrage
// Route : /vigie/locataires/:id
// Données : suiviBailleur/{userId}/locataires/{id} + GED (appel_loyer + quittance_loyer par locataireId)

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  getLocataireById,
  updateLocataire,
  archiverLocataire,
  createPaiement,
} from '../../services/suiviBailleur.service';
import { uploadDocument, getDocuments } from '../../services/document.service';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  Locataire,
} from '../../types/suiviBailleur.types';
import { Document } from '../../types/document.types';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Calendar,
  Home,
  Plus,
  FileText,
  Archive,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Download,
  Building2,
  ChevronRight,
  ExternalLink,
  Edit2,
  Send,
  Clock,
} from 'lucide-react';
import jsPDF from 'jspdf';

// ============================================
// UTILITAIRES
// ============================================

const fmt = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

const fmtDate = (ts: { toDate: () => Date } | undefined | null): string => {
  if (!ts) return 'Non renseignée';
  return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const moisPrecedent = (): string => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const moisCourant = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMoisLabel = (ym: string): string => {
  const [y, m] = ym.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

// ============================================
// TYPES LOCAUX
// ============================================

interface FormQuittance {
  mois: string;
  loyerHC: number;
  charges: number;
  regularisationLibelle: string;
  regularisationMontant: string;
  datePaiement: string;
  modePaiement: string;
  adresseBailleur: string;
}

// Calcule le dernier jour du mois YYYY-MM au format YYYY-MM-DD
const dernierJourDuMois = (mois: string): string => {
  if (!mois) return new Date().toISOString().split('T')[0];
  const [y, m] = mois.split('-').map(Number);
  const d = new Date(y, m, 0); // Jour 0 du mois suivant = dernier jour du mois courant
  return d.toISOString().split('T')[0];
};

// ============================================
// GÉNÉRATION PDF QUITTANCE (jsPDF)
// Modèle Rentila — structure fidèle
// ============================================

const genererQuittancePDF = (
  loc: Locataire,
  bailleurNom: string,
  bailleurPrenom: string,
  bailleurTel: string,
  form: FormQuittance
): Uint8Array => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Police sans emoji, sans caracteres speciaux qui cassent jsPDF
  pdf.setFont('helvetica');

  const moisLabel = formatMoisLabel(form.mois).toUpperCase();
  const loyerHC = form.loyerHC;
  const charges = form.charges;
  const regul = form.regularisationMontant ? parseFloat(form.regularisationMontant.replace(',', '.')) : 0;
  const total = loyerHC + charges + regul;

  const fmtEur = (n: number): string => {
    // toLocaleString génère des espaces insécables que jsPDF affiche mal ("1 /010")
    // Formatage manuel : séparateur milliers = espace simple ASCII, décimale = virgule
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    const parts = abs.toFixed(2).split('.');
    const entier = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return sign + entier + ',' + parts[1] + ' EUR';
  };

  const datePaiementLabel = form.datePaiement
    ? new Date(form.datePaiement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  const W = 210;
  let y = 20;

  // ---- EN-TETE : Bailleur (gauche) / Locataire (droite) ----
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('DE (Bailleur) :', 15, y);
  pdf.text('A (Locataire) :', 115, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${bailleurPrenom} ${bailleurNom}`, 15, y);
  pdf.text(`${loc.prenom} ${loc.nom}`, 115, y);
  y += 5;
  if (form.adresseBailleur) {
    const lignesAdresse = form.adresseBailleur.split('\n');
    lignesAdresse.forEach(ligne => {
      pdf.text(ligne, 15, y);
      y += 4.5;
    });
  }
  if (bailleurTel) {
    pdf.text(`Tel. : ${bailleurTel}`, 15, y);
  }
  if (loc.telephone) {
    pdf.text(`Tel. : ${loc.telephone}`, 115, y);
  }
  y += 5;
  if (loc.email) {
    pdf.text(`Email : ${loc.email}`, 115, y);
  }
  y += 10;

  // ---- SEPARATEUR ----
  pdf.setDrawColor(180, 180, 180);
  pdf.line(15, y, W - 15, y);
  y += 8;

  // ---- TITRE ----
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const titre = `QUITTANCE ${moisLabel}`;
  const titreW = pdf.getTextWidth(titre);
  pdf.text(titre, (W - titreW) / 2, y);
  y += 5;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const sousTitre = `Quittance de loyer pour la periode du 1er au dernier jour de ${formatMoisLabel(form.mois)}`;
  const sousTitreW = pdf.getTextWidth(sousTitre);
  pdf.text(sousTitre, (W - sousTitreW) / 2, y);
  y += 10;

  // ---- MENTION LEGALE ----
  pdf.setFontSize(8);
  pdf.setTextColor(80, 80, 80);
  const mention = 'En cas de conge donne par le locataire, cette quittance ne vaut pas renoncement du bailleur a se prevaloir des obligations du locataire.';
  const lignesMention = pdf.splitTextToSize(mention, W - 30);
  pdf.text(lignesMention, 15, y);
  y += lignesMention.length * 4 + 6;
  pdf.setTextColor(0, 0, 0);

  // ---- SEPARATEUR ----
  pdf.line(15, y, W - 15, y);
  y += 8;

  // ---- DEUX COLONNES : Infos locataire (gauche) + Details terme (droite) ----
  const colG = 15;
  const colD = 115;
  const colW = 80;
  const yTableStart = y;

  // Colonne gauche — Info locataire
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Informations locataire', colG, y);
  y += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Locataire : ${loc.prenom} ${loc.nom}`, colG, y); y += 4.5;
  pdf.text(`Bien loue : ${loc.adresseBien}`, colG, y); y += 4.5;
  if (loc.descriptionLot) { pdf.text(`${loc.descriptionLot}`, colG, y); y += 4.5; }
  const dateEntreeLabel = loc.dateEntree ? fmtDate(loc.dateEntree as any) : '';
  pdf.text(`Entree : ${dateEntreeLabel}`, colG, y); y += 4.5;

  // Colonne droite — Details terme
  let yD = yTableStart;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Details du terme', colD, yD);
  yD += 5;
  pdf.setFont('helvetica', 'normal');

  // Loyer HC
  const libLoyerHC = 'Loyer hors charges :';
  pdf.text(libLoyerHC, colD, yD);
  pdf.text(fmtEur(loyerHC), colD + colW, yD, { align: 'right' });
  yD += 4.5;

  // Charges
  const libCharges = 'Provision sur charges :';
  pdf.text(libCharges, colD, yD);
  pdf.text(fmtEur(charges), colD + colW, yD, { align: 'right' });
  yD += 4.5;

  // Regularisation optionnelle
  if (form.regularisationLibelle && regul !== 0) {
    const libRegul = form.regularisationLibelle || 'Regularisation :';
    pdf.text(libRegul + ' :', colD, yD);
    pdf.text(fmtEur(regul), colD + colW, yD, { align: 'right' });
    yD += 4.5;
  }

  // Ligne separateur colonne droite
  pdf.setDrawColor(180, 180, 180);
  pdf.line(colD, yD, colD + colW, yD);
  yD += 4;

  // Total
  pdf.setFont('helvetica', 'bold');
  pdf.text('Total :', colD, yD);
  pdf.text(fmtEur(total), colD + colW, yD, { align: 'right' });
  yD += 3;

  // Avancer y au max des deux colonnes
  y = Math.max(y, yD) + 10;

  // ---- SEPARATEUR ----
  pdf.setDrawColor(180, 180, 180);
  pdf.line(15, y, W - 15, y);
  y += 8;

  // ---- PAIEMENT ----
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const LABELS_MODE: Record<string, string> = { virement: 'par virement', prelevement: 'par prelevement automatique', cheque: 'par cheque', especes: 'en especes' };
  const modeLabel = form.modePaiement ? (LABELS_MODE[form.modePaiement] || '') : '';
  const txtPaye = `Le locataire ${loc.prenom} ${loc.nom} a regle la somme de ${fmtEur(total)} le ${datePaiementLabel}${modeLabel ? ' ' + modeLabel : ''}.`;
  pdf.text(pdf.splitTextToSize(txtPaye, W - 30), 15, y);
  y += 10;

  // TOTAL PAYE encadre
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.rect(15, y, W - 30, 12);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  const txtTotal = `TOTAL PAYE : ${fmtEur(total)}`;
  const txtTotalW = pdf.getTextWidth(txtTotal);
  pdf.text(txtTotal, (W - txtTotalW) / 2, y + 8);
  y += 18;

  // ---- SEPARATEUR ----
  pdf.setLineWidth(0.3);
  pdf.setDrawColor(180, 180, 180);
  pdf.line(15, y, W - 15, y);
  y += 8;

  // ---- INFOS BAS ----
  pdf.setFontSize(8.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);
  pdf.text(`Bien : ${loc.adresseBien}`, 15, y); y += 5;
  pdf.text(`Periode : ${formatMoisLabel(form.mois)}`, 15, y); y += 5;
  if (loc.depotGarantie) {
    pdf.text(`Depot de garantie verse : ${fmtEur(loc.depotGarantie)}`, 15, y); y += 5;
  }

  // ---- SIGNATURE BAILLEUR (bas droite) ----
  y += 15;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  pdf.text('Fait le ' + new Date().toLocaleDateString('fr-FR'), W - 15 - 60, y);
  y += 5;
  pdf.text(`${bailleurPrenom} ${bailleurNom}`, W - 15 - 60, y);
  y += 5;
  pdf.text('(Signature bailleur)', W - 15 - 60, y);

  // Footer
  y = 285;
  pdf.setFontSize(7);
  pdf.setTextColor(150, 150, 150);
  const footer = 'Document genere par Copro Protect — copro-protect.fr';
  const footerW = pdf.getTextWidth(footer);
  pdf.text(footer, (W - footerW) / 2, y);

  return pdf.output('arraybuffer') as unknown as Uint8Array;
};

// ============================================
// GÉNÉRATION PDF APPEL DE LOYER (jsPDF)
// Modèle "Avis d'échéance" — avant réception du paiement
// ============================================

const genererAppelPDF = (
  loc: Locataire,
  bailleurNom: string,
  bailleurPrenom: string,
  bailleurTel: string,
  form: FormQuittance
): Uint8Array => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  pdf.setFont('helvetica');

  const moisLabel = formatMoisLabel(form.mois).toUpperCase();
  const loyerHC = form.loyerHC;
  const charges = form.charges;
  const regul = form.regularisationMontant ? parseFloat(form.regularisationMontant.replace(',', '.')) : 0;
  const total = loyerHC + charges + regul;

  const fmtEur = (n: number): string => {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    const parts = abs.toFixed(2).split('.');
    const entier = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return sign + entier + ',' + parts[1] + ' EUR';
  };

  const W = 210;
  let y = 20;

  // En-tete bailleur / locataire
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('DE (Bailleur) :', 15, y);
  pdf.text('A (Locataire) :', 115, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${bailleurPrenom} ${bailleurNom}`, 15, y);
  pdf.text(`${loc.prenom} ${loc.nom}`, 115, y);
  y += 5;
  if (form.adresseBailleur) {
    const lignes = form.adresseBailleur.split('\n');
    lignes.forEach(ligne => { pdf.text(ligne, 15, y); y += 4.5; });
  }
  if (bailleurTel) pdf.text(`Tel. : ${bailleurTel}`, 15, y);
  if (loc.telephone) pdf.text(`Tel. : ${loc.telephone}`, 115, y);
  y += 5;
  if (loc.email) pdf.text(`Email : ${loc.email}`, 115, y);
  y += 10;

  pdf.setDrawColor(180, 180, 180);
  pdf.line(15, y, W - 15, y);
  y += 8;

  // Titre
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const titreAppel = `AVIS D'ECHEANCE ${moisLabel}`;
  const titreW = pdf.getTextWidth(titreAppel);
  pdf.text(titreAppel, (W - titreW) / 2, y);
  y += 5;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const sousTitre = `Appel de loyer pour la periode du 1er au dernier jour de ${formatMoisLabel(form.mois)}`;
  const sousTitreW = pdf.getTextWidth(sousTitre);
  pdf.text(sousTitre, (W - sousTitreW) / 2, y);
  y += 10;

  pdf.setFontSize(8);
  pdf.setTextColor(80, 80, 80);
  const mention = `Cet avis d'echeance vous est adresse a titre indicatif. Il ne constitue pas une quittance de loyer.`;
  const lignesMention = pdf.splitTextToSize(mention, W - 30);
  pdf.text(lignesMention, 15, y);
  y += lignesMention.length * 4 + 6;
  pdf.setTextColor(0, 0, 0);

  pdf.setDrawColor(180, 180, 180);
  pdf.line(15, y, W - 15, y);
  y += 8;

  // Deux colonnes
  const colG = 15;
  const colD = 115;
  const colW = 80;
  const yTableStart = y;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Informations locataire', colG, y);
  y += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Locataire : ${loc.prenom} ${loc.nom}`, colG, y); y += 4.5;
  pdf.text(`Bien loue : ${loc.adresseBien}`, colG, y); y += 4.5;
  if (loc.descriptionLot) { pdf.text(`${loc.descriptionLot}`, colG, y); y += 4.5; }
  const dateEntreeLabel = loc.dateEntree ? fmtDate(loc.dateEntree as any) : '';
  pdf.text(`Entree : ${dateEntreeLabel}`, colG, y);

  let yD = yTableStart;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Details du terme', colD, yD);
  yD += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.text('Loyer hors charges :', colD, yD);
  pdf.text(fmtEur(loyerHC), colD + colW, yD, { align: 'right' });
  yD += 4.5;
  pdf.text('Provision sur charges :', colD, yD);
  pdf.text(fmtEur(charges), colD + colW, yD, { align: 'right' });
  yD += 4.5;
  if (form.regularisationLibelle && regul !== 0) {
    pdf.text((form.regularisationLibelle || 'Regularisation') + ' :', colD, yD);
    pdf.text(fmtEur(regul), colD + colW, yD, { align: 'right' });
    yD += 4.5;
  }
  pdf.setDrawColor(180, 180, 180);
  pdf.line(colD, yD, colD + colW, yD);
  yD += 4;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Total appele :', colD, yD);
  pdf.text(fmtEur(total), colD + colW, yD, { align: 'right' });

  y = Math.max(y, yD) + 10;
  pdf.setDrawColor(180, 180, 180);
  pdf.line(15, y, W - 15, y);
  y += 8;

  // Encadre total
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.rect(15, y, W - 30, 12);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  const txtTotal = `TOTAL A REGLER : ${fmtEur(total)}`;
  const txtTotalW = pdf.getTextWidth(txtTotal);
  pdf.text(txtTotal, (W - txtTotalW) / 2, y + 8);
  y += 20;

  pdf.setFontSize(8.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);
  pdf.text(`Bien : ${loc.adresseBien}`, 15, y); y += 5;
  pdf.text(`Periode : ${formatMoisLabel(form.mois)}`, 15, y); y += 5;
  if (loc.depotGarantie) {
    pdf.text(`Depot de garantie verse : ${fmtEur(loc.depotGarantie)}`, 15, y); y += 5;
  }

  y = 285;
  pdf.setFontSize(7);
  pdf.setTextColor(150, 150, 150);
  const footer = 'Document genere par Copro Protect — copro-protect.fr';
  const footerW = pdf.getTextWidth(footer);
  pdf.text(footer, (W - footerW) / 2, y);

  return pdf.output('arraybuffer') as unknown as Uint8Array;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function FicheLocataireVigiePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { firebaseUser, userProfile } = useAuth();
  const { coproprieteId } = useCopropriete();

  const [locataire, setLocataire] = useState<Locataire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modales
  const [showModalAppel, setShowModalAppel] = useState(false);
  const [showModalQuittance, setShowModalQuittance] = useState(false);
  const [showModalArchive, setShowModalArchive] = useState(false);
  const [showModalEdition, setShowModalEdition] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [appelGenere, setAppelGenere] = useState(false);
  const [appelDocId, setAppelDocId] = useState<string | null>(null);
  const [quittanceGeneree, setQuittanceGeneree] = useState(false);
  const [quittanceDocId, setQuittanceDocId] = useState<string | null>(null);
  // Appel en cours de conversion → quittance
  const [appelAConvertir, setAppelAConvertir] = useState<Document | null>(null);

  // GED — appels et quittances séparés
  const [appelsGED, setAppelsGED] = useState<Document[]>([]);
  const [quittancesGED, setQuittancesGED] = useState<Document[]>([]);
  const [loadingGED, setLoadingGED] = useState(false);

  // Adresse bailleur — mémorisée dans userPreferences
  const [adresseBailleurSauvee, setAdresseBailleurSauvee] = useState('');

  // Formulaire quittance
  const [formQ, setFormQ] = useState<FormQuittance>({
    mois: moisCourant(),
    loyerHC: 0,
    charges: 0,
    regularisationLibelle: '',
    regularisationMontant: '',
    datePaiement: dernierJourDuMois(moisCourant()),
    modePaiement: 'virement',
    adresseBailleur: '',
  });

  // Formulaire édition locataire
  const [formEdit, setFormEdit] = useState({
    nom: '', prenom: '', email: '', telephone: '',
    adresseBien: '', descriptionLot: '',
    loyerMensuel: 0, chargesProvision: 0, depotGarantie: 0,
  });

  // ============================================
  // CHARGEMENT
  // ============================================

  const charger = useCallback(async () => {
    if (!firebaseUser || !id) return;
    setLoading(true);
    try {
      const loc = await getLocataireById(firebaseUser.uid, id);
      if (!loc) { setError('Locataire introuvable'); setLoading(false); return; }
      setLocataire(loc);
      setFormEdit({
        nom: loc.nom || '',
        prenom: loc.prenom || '',
        email: loc.email || '',
        telephone: loc.telephone || '',
        adresseBien: loc.adresseBien || '',
        descriptionLot: loc.descriptionLot || '',
        loyerMensuel: loc.loyerMensuel || 0,
        chargesProvision: loc.chargesProvision || 0,
        depotGarantie: loc.depotGarantie || 0,
      });

      // Pré-remplir formulaire quittance depuis le locataire
      setFormQ(f => ({
        ...f,
        loyerHC: loc.loyerMensuel || 0,
        charges: loc.chargesProvision || 0,
        datePaiement: dernierJourDuMois(f.mois || moisCourant()),
      }));

      // Charger adresse bailleur sauvegardée
      const prefRef = doc(db, 'userPreferences', firebaseUser.uid);
      const prefSnap = await getDoc(prefRef);
      if (prefSnap.exists()) {
        const adresse = prefSnap.data()?.adresseBailleur || '';
        setAdresseBailleurSauvee(adresse);
        setFormQ(f => ({ ...f, adresseBailleur: adresse }));
      }
    } catch (e) {
      console.error('[FicheLocataire]', e);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, id]);

  useEffect(() => { charger(); }, [charger]);

  // ============================================
  // CHARGER APPELS + QUITTANCES GED
  // ============================================

  const chargerDocumentsGED = React.useCallback(async () => {
    if (!coproprieteId || !id) return;
    setLoadingGED(true);
    try {
      // Catégorie mon_bien : contient appels et quittances liés à ce locataire
      const docs = await getDocuments(coproprieteId, { categorie: 'mon_bien' });
      const parLocataire = docs.filter(d => (d as any).locataireId === id && !d.estArchive);
      const tri = (a: Document, b: Document) => {
        const da = (a.dateDocument as any)?.toDate?.()?.getTime() || 0;
        const db2 = (b.dateDocument as any)?.toDate?.()?.getTime() || 0;
        return db2 - da;
      };
      setAppelsGED(parLocataire.filter(d => (d.type as string) === 'appel_loyer').sort(tri));
      setQuittancesGED(parLocataire.filter(d => (d.type as string) === 'quittance_loyer').sort(tri));
    } catch (e) {
      console.error('[FicheLocataire] Erreur chargement GED:', e);
    } finally {
      setLoadingGED(false);
    }
  }, [coproprieteId, id]);

  useEffect(() => { chargerDocumentsGED(); }, [chargerDocumentsGED]);

  // ============================================
  // MODIFIER LOCATAIRE
  // ============================================

  const handleModifierLocataire = async () => {
    if (!firebaseUser || !id) return;
    if (!formEdit.nom.trim() || !formEdit.prenom.trim() || !formEdit.adresseBien.trim()) {
      setSavingError('Nom, prénom et adresse obligatoires'); return;
    }
    setSaving(true); setSavingError(null);
    try {
      await updateLocataire(firebaseUser.uid, id, {
        nom: formEdit.nom,
        prenom: formEdit.prenom,
        email: formEdit.email || undefined,
        telephone: formEdit.telephone || undefined,
        adresseBien: formEdit.adresseBien,
        descriptionLot: formEdit.descriptionLot || undefined,
        loyerMensuel: formEdit.loyerMensuel,
        chargesProvision: formEdit.chargesProvision,
        depotGarantie: formEdit.depotGarantie || undefined,
      });
      setShowModalEdition(false);
      await charger();
    } catch {
      setSavingError('Impossible de modifier le locataire');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // GÉNÉRER APPEL DE LOYER PDF
  // ============================================

  const handleGenererAppel = async () => {
    if (!locataire || !firebaseUser || !userProfile) return;
    setSaving(true); setSavingError(null);
    setAppelDocId(null);

    try {
      // Sauvegarder adresse bailleur si nouvelle
      if (formQ.adresseBailleur && formQ.adresseBailleur !== adresseBailleurSauvee) {
        const prefRef = doc(db, 'userPreferences', firebaseUser.uid);
        await setDoc(prefRef, { adresseBailleur: formQ.adresseBailleur }, { merge: true });
        setAdresseBailleurSauvee(formQ.adresseBailleur);
      }

      const pdfBytes = genererAppelPDF(
        locataire,
        userProfile.nom || '',
        userProfile.prenom || '',
        userProfile.gsm || '',
        formQ
      );

      const moisLabel = formatMoisLabel(formQ.mois);
      const nomFichier = `AppelLoyer_${moisLabel.replace(' ', '_')}_${locataire.nom}.pdf`;
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });

      // Téléchargement navigateur
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url; a.download = nomFichier; a.click();
      URL.revokeObjectURL(url);

      // Archivage GED — catégorie mon_bien, type appel_loyer
      try {
        const fichier = new File([blob], nomFichier, { type: 'application/pdf' });
        const titreGED = `Appel loyer ${moisLabel} - ${locataire.prenom} ${locataire.nom}`;
        const docId = await uploadDocument({
          coproprieteId: coproprieteId || firebaseUser.uid,
          file: fichier,
          titre: titreGED,
          type: 'appel_loyer',
          categorie: 'mon_bien',
          description: `Avis d'échéance — ${locataire.adresseBien}`,
          dateDocument: formQ.mois ? new Date(`${formQ.mois}-01`) : new Date(),
          userId: firebaseUser.uid,
          userNom: `${userProfile.prenom || ''} ${userProfile.nom || ''}`.trim(),
          visibilite: 'proprietaire',
          source: 'appel_auto',
          typeDocumentLabel: 'Appel de loyer',
          skipDuplicateCheck: false,
          locataireId: id,
          locataireEmail: locataire.email || '',
          moisConcerne: formQ.mois,
          loyerHC: formQ.loyerHC,
          charges: formQ.charges,
          total: formQ.loyerHC + formQ.charges + (parseFloat(formQ.regularisationMontant.replace(',', '.')) || 0),
        });
        setAppelDocId(docId);
        await chargerDocumentsGED();
      } catch (archiveErr: any) {
        if (String(archiveErr?.message).startsWith('DOUBLON_DETECTE')) {
          const parts = String(archiveErr.message).split('|');
          setAppelDocId(parts[2] || null);
        } else {
          console.warn('[Appel] Archivage GED échoué (non bloquant):', archiveErr);
        }
      }

      setAppelGenere(true);
    } catch (e) {
      console.error('[Appel]', e);
      setSavingError("Erreur lors de la génération de l'appel de loyer");
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // CONVERTIR APPEL → QUITTANCE
  // ============================================

  const handleConvertirEnQuittance = async (appel: Document) => {
    if (!locataire || !firebaseUser || !userProfile) return;
    setSaving(true); setSavingError(null);
    setQuittanceDocId(null);

    // Pré-remplir formQ depuis les données stockées sur l'appel GED
    const appelData = appel as any;
    const moisConv = appelData.moisConcerne || formQ.mois;
    const formConversion: FormQuittance = {
      mois: moisConv,
      loyerHC: appelData.loyerHC ?? locataire.loyerMensuel ?? 0,
      charges: appelData.charges ?? locataire.chargesProvision ?? 0,
      regularisationLibelle: '',
      regularisationMontant: '',
      datePaiement: dernierJourDuMois(moisConv),
      modePaiement: 'virement',
      adresseBailleur: adresseBailleurSauvee || formQ.adresseBailleur,
    };

    try {
      const pdfBytes = genererQuittancePDF(
        locataire,
        userProfile.nom || '',
        userProfile.prenom || '',
        userProfile.gsm || '',
        formConversion
      );

      const moisLabel = formatMoisLabel(formConversion.mois);
      const nomFichier = `Quittance_${moisLabel.replace(' ', '_')}_${locataire.nom}.pdf`;
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });

      // Téléchargement
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url; a.download = nomFichier; a.click();
      URL.revokeObjectURL(url);

      // Archivage GED quittance
      try {
        const fichier = new File([blob], nomFichier, { type: 'application/pdf' });
        const titreGED = `Quittance ${moisLabel} - ${locataire.prenom} ${locataire.nom}`;
        const totalQuittance = formConversion.loyerHC + formConversion.charges;
        const docId = await uploadDocument({
          coproprieteId: coproprieteId || firebaseUser.uid,
          file: fichier,
          titre: titreGED,
          type: 'quittance_loyer',
          categorie: 'mon_bien',
          description: `Quittance de loyer — ${locataire.adresseBien}`,
          dateDocument: new Date(`${formConversion.mois}-01`),
          userId: firebaseUser.uid,
          userNom: `${userProfile.prenom || ''} ${userProfile.nom || ''}`.trim(),
          visibilite: 'proprietaire',
          source: 'quittance_auto',
          typeDocumentLabel: 'Quittance de loyer',
          skipDuplicateCheck: false,
          locataireId: id,
          moisConcerne: formConversion.mois,
          loyerHC: formConversion.loyerHC,
          charges: formConversion.charges,
          total: totalQuittance,
        });
        setQuittanceDocId(docId);

        // Créer paiement Firestore
        try {
          await createPaiement(firebaseUser.uid, id!, {
            type: 'loyer',
            moisConcerne: formConversion.mois,
            montantAttendu: totalQuittance,
            montantRecu: totalQuittance,
          });
        } catch (e) {
          console.warn('[Conversion] Paiement auto échoué:', e);
        }

        await chargerDocumentsGED();
      } catch (archiveErr: any) {
        if (String(archiveErr?.message).startsWith('DOUBLON_DETECTE')) {
          const parts = String(archiveErr.message).split('|');
          setQuittanceDocId(parts[2] || null);
        } else {
          console.warn('[Conversion] Archivage quittance échoué:', archiveErr);
        }
      }

      setAppelAConvertir(null);
      setQuittanceGeneree(true);
      setShowModalQuittance(true);
    } catch (e) {
      console.error('[Conversion]', e);
      setSavingError('Erreur lors de la conversion en quittance');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // GÉNÉRER QUITTANCE PDF
  // ============================================

  const handleGenererQuittance = async () => {
    if (!locataire || !firebaseUser || !userProfile) return;
    setSaving(true); setSavingError(null);
    setQuittanceDocId(null);

    try {
      // Sauvegarder adresse bailleur si nouvelle
      if (formQ.adresseBailleur && formQ.adresseBailleur !== adresseBailleurSauvee) {
        const prefRef = doc(db, 'userPreferences', firebaseUser.uid);
        await setDoc(prefRef, { adresseBailleur: formQ.adresseBailleur }, { merge: true });
        setAdresseBailleurSauvee(formQ.adresseBailleur);
      }

      const pdfBytes = genererQuittancePDF(
        locataire,
        userProfile.nom || '',
        userProfile.prenom || '',
        userProfile.gsm || '',
        formQ
      );

      const moisLabel = formatMoisLabel(formQ.mois);
      const nomFichier = `Quittance_${moisLabel.replace(' ', '_')}_${locataire.nom}.pdf`;
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });

      // Téléchargement navigateur
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = nomFichier;
      a.click();
      URL.revokeObjectURL(url);

      // Archivage automatique dans la GED + création paiement auto
      try {
        const fichier = new File([blob], nomFichier, { type: 'application/pdf' });
        const titreGED = `Quittance ${moisLabel} - ${locataire.prenom} ${locataire.nom}`;
        const totalQuittance = formQ.loyerHC + formQ.charges + (parseFloat(formQ.regularisationMontant.replace(',', '.')) || 0);
        const docId = await uploadDocument({
          coproprieteId: coproprieteId || firebaseUser.uid,
          file: fichier,
          titre: titreGED,
          type: 'quittance_loyer',
          categorie: 'mon_bien',
          description: `Quittance de loyer — ${locataire.adresseBien}`,
          dateDocument: formQ.mois ? new Date(`${formQ.mois}-01`) : new Date(),
          userId: firebaseUser.uid,
          userNom: `${userProfile.prenom || ''} ${userProfile.nom || ''}`.trim(),
          visibilite: 'proprietaire',
          source: 'quittance_auto',
          typeDocumentLabel: 'Quittance de loyer',
          skipDuplicateCheck: false,
          locataireId: id,
          moisConcerne: formQ.mois,
          loyerHC: formQ.loyerHC,
          charges: formQ.charges,
          total: totalQuittance,
        });
        setQuittanceDocId(docId);

        // Créer le paiement automatiquement dans Firestore
        try {
          await createPaiement(firebaseUser.uid, id!, {
            type: 'loyer',
            moisConcerne: formQ.mois,
            montantAttendu: totalQuittance,
            montantRecu: totalQuittance,
          });
        } catch (paiementErr) {
          console.warn('[Quittance] Création paiement auto échouée (non bloquant):', paiementErr);
        }

        // Recharger GED
        await chargerDocumentsGED();
      } catch (archiveErr: any) {
        // Doublon détecté — quittance déjà archivée pour ce mois
        if (String(archiveErr?.message).startsWith('DOUBLON_DETECTE')) {
          const parts = String(archiveErr.message).split('|');
          setQuittanceDocId(parts[2] || null);
        } else {
          console.warn('[Quittance] Archivage GED échoué (non bloquant):', archiveErr);
        }
      }

      setQuittanceGeneree(true);
    } catch (e) {
      console.error('[Quittance]', e);
      setSavingError('Erreur lors de la génération du PDF');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // ARCHIVER LOCATAIRE
  // ============================================

  const handleArchiver = async () => {
    if (!firebaseUser || !id) return;
    setSaving(true);
    try {
      await archiverLocataire(firebaseUser.uid, id);
      navigate('/vigie/comptabilite');
    } catch {
      setSavingError('Impossible d\'archiver le locataire');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // RENDU ÉTATS
  // ============================================

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      <span className="ml-3 text-gray-500 text-sm">Chargement...</span>
    </div>
  );

  if (error || !locataire) return (
    <div className="max-w-2xl mx-auto py-12 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
      <p className="text-gray-700 font-semibold">{error || 'Locataire introuvable'}</p>
      <button onClick={() => navigate('/vigie/comptabilite')} className="mt-4 text-sm text-blue-600 hover:underline">
        Retour à Ma comptabilité
      </button>
    </div>
  );

  // Pas de calculs sur paiements — section historique remplacée par quittances GED

  // ============================================
  // RENDU PRINCIPAL
  // ============================================

  return (
    <div className="max-w-3xl mx-auto space-y-5">

      {/* Navigation retour */}
      <button
        onClick={() => navigate('/vigie/comptabilite')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Ma comptabilité
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-800 font-medium">{locataire.prenom} {locataire.nom}</span>
      </button>

      {/* En-tête fiche */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-xl">
              {locataire.prenom?.charAt(0)?.toUpperCase() || 'L'}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{locataire.prenom} {locataire.nom}</h1>
              <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                <Home className="w-3.5 h-3.5" />
                {locataire.adresseBien}
              </p>
              {locataire.descriptionLot && (
                <p className="text-xs text-gray-400 mt-0.5">{locataire.descriptionLot}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSavingError(null); setShowModalEdition(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg text-xs font-medium transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" /> Modifier
            </button>
            {locataire.actif && (
              <button
                onClick={() => setShowModalArchive(true)}
                className="flex items-center justify-center w-7 h-7 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Archiver ce locataire"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
            )}
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${locataire.actif ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {locataire.actif ? 'Actif' : 'Archivé'}
            </span>
          </div>
        </div>

        {/* Coordonnées */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-600 mb-5">
          {locataire.email && (
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{locataire.email}</span>
            </div>
          )}
          {locataire.telephone && (
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span>{locataire.telephone}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>Entrée le {fmtDate(locataire.dateEntree as any)}</span>
          </div>
        </div>

        {/* Infos bail */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Loyer HC', valeur: fmt(locataire.loyerMensuel || 0) },
            { label: 'Charges', valeur: fmt(locataire.chargesProvision || 0) },
            { label: 'Total mensuel', valeur: fmt((locataire.loyerMensuel || 0) + (locataire.chargesProvision || 0)) },
            { label: 'Dépôt garantie', valeur: locataire.depotGarantie ? fmt(locataire.depotGarantie) : 'Non renseigné' },
          ].map(({ label, valeur }) => (
            <div key={label} className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-sm font-bold text-gray-800">{valeur}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Actions principales */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => { setSavingError(null); setAppelGenere(false); setShowModalAppel(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors"
        >
          <Send className="w-4 h-4" /> Créer un appel de loyer
        </button>
        <button
          onClick={() => { setSavingError(null); setQuittanceGeneree(false); setShowModalQuittance(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors"
        >
          <FileText className="w-4 h-4" /> Émettre une quittance directe
        </button>
      </div>

      {/* ---- Section Appels de loyer ---- */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <h2 className="font-semibold text-gray-800 text-sm">Appels de loyer</h2>
          </div>
          <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium">
            {appelsGED.length} émis{appelsGED.length > 1 ? '' : ''}
          </span>
        </div>

        {loadingGED ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
          </div>
        ) : appelsGED.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm font-semibold text-gray-600 mb-1">Aucun appel émis</p>
            <p className="text-xs text-gray-400">Créez un appel de loyer — il apparaîtra ici pour suivi et conversion.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {appelsGED.map(a => {
              const dateDoc = (a.dateDocument as any)?.toDate?.();
              const dateLabel = dateDoc ? dateDoc.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Date inconnue';
              const total = (a as any).total;
              const email = (a as any).locataireEmail || locataire.email || '';
              const sujetMail = encodeURIComponent(`Appel de loyer ${dateLabel} — ${locataire.adresseBien}`);
              const bodyMail = encodeURIComponent(`Bonjour ${locataire.prenom},

Veuillez trouver ci-joint votre appel de loyer pour ${dateLabel}.

Cordialement`);
              // Vérifier si une quittance existe déjà pour ce mois
              const moisAppel = (a as any).moisConcerne || '';
              const dejaConverti = quittancesGED.some(q => (q as any).moisConcerne === moisAppel);
              return (
                <div key={a.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800 capitalize">{dateLabel}</p>
                        {dejaConverti ? (
                          <span className="px-1.5 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 font-medium">Converti</span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">En attente</span>
                        )}
                      </div>
                      {total !== undefined && (
                        <p className="text-xs text-gray-500 mt-0.5">{fmt(total)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(a as any).url || (a as any).fileUrl ? (
                        <button
                          onClick={() => window.open((a as any).url || (a as any).fileUrl, '_blank')}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" /> Voir
                        </button>
                      ) : null}
                      {email && (
                        <a
                          href={`https://mail.google.com/mail/?view=cm&to=${email}&su=${sujetMail}&body=${bodyMail}`}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <Send className="w-3 h-3" /> Envoyer
                        </a>
                      )}
                      {!dejaConverti && (
                        <button
                          onClick={() => handleConvertirEnQuittance(a)}
                          disabled={saving}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle className="w-3 h-3" /> Convertir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- Section Quittances émises ---- */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <h2 className="font-semibold text-gray-800 text-sm">Quittances émises</h2>
          </div>
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">
            {quittancesGED.length} émise{quittancesGED.length > 1 ? 's' : ''}
          </span>
        </div>

        {loadingGED ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
          </div>
        ) : quittancesGED.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm font-semibold text-gray-600 mb-1">Aucune quittance émise</p>
            <p className="text-xs text-gray-400">Convertissez un appel reçu, ou émettez une quittance directe.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {quittancesGED.map(q => {
              const dateDoc = (q.dateDocument as any)?.toDate?.();
              const dateLabel = dateDoc ? dateDoc.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Date inconnue';
              const total = (q as any).total;
              const email = locataire.email || '';
              const sujetMail = encodeURIComponent(`Quittance de loyer ${dateLabel} — ${locataire.adresseBien}`);
              const bodyMail = encodeURIComponent(`Bonjour ${locataire.prenom},

Veuillez trouver ci-joint votre quittance de loyer pour ${dateLabel}.

Cordialement`);
              return (
                <div key={q.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-800 capitalize">{dateLabel}</p>
                      <span className="px-1.5 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 font-medium">Réglé</span>
                    </div>
                    {total !== undefined && (
                      <p className="text-xs text-gray-500 mt-0.5">{fmt(total)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(q as any).url || (q as any).fileUrl ? (
                      <button
                        onClick={() => window.open((q as any).url || (q as any).fileUrl, '_blank')}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> Voir
                      </button>
                    ) : null}
                    {email && (
                      <a
                        href={`https://mail.google.com/mail/?view=cm&to=${email}&su=${sujetMail}&body=${bodyMail}`}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <Send className="w-3 h-3" /> Envoyer
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


      {/* ============================================ */}
      {/* MODALE ÉDITION LOCATAIRE                   */}
      {/* ============================================ */}

      {showModalEdition && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModalEdition(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Modifier le locataire</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(['prenom', 'nom'] as const).map(k => (
                <div key={k}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{k === 'prenom' ? 'Prénom *' : 'Nom *'}</label>
                  <input type="text" value={formEdit[k]}
                    onChange={e => setFormEdit(f => ({ ...f, [k]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse du bien loué *</label>
                <input type="text" value={formEdit.adresseBien}
                  onChange={e => setFormEdit(f => ({ ...f, adresseBien: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
                <input type="text" value={formEdit.descriptionLot}
                  onChange={e => setFormEdit(f => ({ ...f, descriptionLot: e.target.value }))}
                  placeholder="Ex : Appartement T4 RDC n°3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={formEdit.email}
                  onChange={e => setFormEdit(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input type="tel" value={formEdit.telephone}
                  onChange={e => setFormEdit(f => ({ ...f, telephone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
              </div>
              {[
                { label: 'Loyer mensuel HC (€)', key: 'loyerMensuel' },
                { label: 'Charges provision (€/mois)', key: 'chargesProvision' },
                { label: 'Dépôt de garantie (€)', key: 'depotGarantie' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type="number" value={(formEdit as any)[key]}
                    onChange={e => setFormEdit(f => ({ ...f, [key]: parseFloat(e.target.value) || 0 }))}
                    min="0" step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                </div>
              ))}
            </div>
            {savingError && <p className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded-lg">{savingError}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModalEdition(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Annuler
              </button>
              <button onClick={handleModifierLocataire} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>,
        window.document.body
      )}

      {/* ============================================ */}
      {/* ============================================ */}
      {/* MODALE APPEL DE LOYER                       */}
      {/* ============================================ */}

      {showModalAppel && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModalAppel(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
                <Send className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Créer un appel de loyer</h2>
                <p className="text-xs text-gray-500">{locataire.prenom} {locataire.nom}</p>
              </div>
            </div>

            {appelGenere ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">Appel de loyer généré</h3>
                <p className="text-sm text-gray-500 mb-1">PDF téléchargé et archivé dans Mes documents.</p>
                {locataire.email && (
                  <p className="text-xs text-blue-600 mb-4">
                    Utilisez le bouton "Envoyer" dans la liste pour le transmettre par mail.
                  </p>
                )}
                <div className="flex justify-center gap-3">
                  <button onClick={() => { setAppelGenere(false); }} className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                    <Download className="w-4 h-4" /> Générer un autre
                  </button>
                  <button onClick={() => setShowModalAppel(false)} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600">
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mois concerné *</label>
                    <input type="month" value={formQ.mois}
                      onChange={e => setFormQ(f => ({ ...f, mois: e.target.value, datePaiement: dernierJourDuMois(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Loyer HC (€) *</label>
                      <input type="number" value={formQ.loyerHC}
                        onChange={e => setFormQ(f => ({ ...f, loyerHC: parseFloat(e.target.value) || 0 }))}
                        min="0" step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Charges (€) *</label>
                      <input type="number" value={formQ.charges}
                        onChange={e => setFormQ(f => ({ ...f, charges: parseFloat(e.target.value) || 0 }))}
                        min="0" step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Régularisation (optionnel)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Libellé</label>
                        <input type="text" value={formQ.regularisationLibelle}
                          onChange={e => setFormQ(f => ({ ...f, regularisationLibelle: e.target.value }))}
                          placeholder="Ex: Régularisation 2024"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Montant (€)</label>
                        <input type="text" value={formQ.regularisationMontant}
                          onChange={e => setFormQ(f => ({ ...f, regularisationMontant: e.target.value }))}
                          placeholder="Ex: -45,00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse du bailleur *
                      {adresseBailleurSauvee && <span className="ml-2 text-xs text-emerald-600 font-normal">(mémorisée)</span>}
                    </label>
                    <textarea value={formQ.adresseBailleur}
                      onChange={e => setFormQ(f => ({ ...f, adresseBailleur: e.target.value }))}
                      placeholder="Ex: 12 rue des Lilas, 62000 Arras"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none resize-none" />
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Loyer HC</span>
                      <span className="font-medium">{fmt(formQ.loyerHC)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Charges</span>
                      <span className="font-medium">{fmt(formQ.charges)}</span>
                    </div>
                    {formQ.regularisationMontant && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">{formQ.regularisationLibelle || 'Régularisation'}</span>
                        <span className="font-medium">{fmt(parseFloat(formQ.regularisationMontant.replace(',', '.')) || 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold mt-2 pt-2 border-t border-amber-200">
                      <span>Total appelé</span>
                      <span className="text-amber-700">
                        {fmt(formQ.loyerHC + formQ.charges + (parseFloat(formQ.regularisationMontant.replace(',', '.')) || 0))}
                      </span>
                    </div>
                  </div>
                </div>
                {savingError && <p className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded-lg">{savingError}</p>}
                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => setShowModalAppel(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
                  <button onClick={handleGenererAppel} disabled={saving || !formQ.mois || !formQ.adresseBailleur}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Générer l'appel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        window.document.body
      )}

      {/* ============================================ */}
      {/* MODALE QUITTANCE PDF                        */}
      {/* ============================================ */}

      {showModalQuittance && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { if (!saving) setShowModalQuittance(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 mx-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-900">Générer une quittance</h2>
                <p className="text-xs text-gray-500">{locataire.prenom} {locataire.nom}</p>
              </div>
            </div>

            {quittanceGeneree ? (
              <div className="text-center py-4">
                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-gray-800 mb-1">Quittance générée !</p>
                <p className="text-sm text-gray-500 mb-1">PDF téléchargé dans vos téléchargements.</p>
                <p className="text-sm text-emerald-600 font-medium mb-4">
                  {quittanceDocId ? 'Archivée automatiquement dans Mes documents.' : 'Archivage GED en cours...'}
                </p>
                <div className="flex justify-center gap-3">
                  <button onClick={() => { setQuittanceGeneree(false); }}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                    <Download className="w-4 h-4" /> Générer une autre
                  </button>
                  <button onClick={() => setShowModalQuittance(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2.5">
                  {/* Ligne 1 : Mois + Loyer HC + Charges */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Mois *</label>
                      <input type="month" value={formQ.mois}
                        onChange={e => setFormQ(f => ({ ...f, mois: e.target.value, datePaiement: dernierJourDuMois(e.target.value) }))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Loyer HC (€) *</label>
                      <input type="number" value={formQ.loyerHC}
                        onChange={e => setFormQ(f => ({ ...f, loyerHC: parseFloat(e.target.value) || 0 }))}
                        min="0" step="0.01"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Charges (€) *</label>
                      <input type="number" value={formQ.charges}
                        onChange={e => setFormQ(f => ({ ...f, charges: parseFloat(e.target.value) || 0 }))}
                        min="0" step="0.01"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                    </div>
                  </div>

                  {/* Ligne 2 : Date réception + Mode règlement */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Date de réception *</label>
                      <input type="date" value={formQ.datePaiement}
                        onChange={e => setFormQ(f => ({ ...f, datePaiement: e.target.value }))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Mode de règlement</label>
                      <select value={formQ.modePaiement}
                        onChange={e => setFormQ(f => ({ ...f, modePaiement: e.target.value }))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none">
                        <option value="virement">Virement</option>
                        <option value="prelevement">Prélèvement auto</option>
                        <option value="cheque">Chèque</option>
                        <option value="especes">Espèces</option>
                      </select>
                    </div>
                  </div>

                  {/* Régularisation — compacte, repliée visuellement */}
                  <details className="bg-gray-50 rounded-lg">
                    <summary className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none">
                      Régularisation (optionnel)
                    </summary>
                    <div className="px-3 pb-3 grid grid-cols-2 gap-2 mt-1">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Libellé</label>
                        <input type="text" value={formQ.regularisationLibelle}
                          onChange={e => setFormQ(f => ({ ...f, regularisationLibelle: e.target.value }))}
                          placeholder="Ex: Régul. charges 2024"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Montant (€)</label>
                        <input type="text" value={formQ.regularisationMontant}
                          onChange={e => setFormQ(f => ({ ...f, regularisationMontant: e.target.value }))}
                          placeholder="Ex: -45,00"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                      </div>
                    </div>
                  </details>

                  {/* Adresse bailleur */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Adresse du bailleur *
                      {adresseBailleurSauvee && <span className="ml-2 text-xs text-emerald-600 font-normal">(mémorisée)</span>}
                    </label>
                    <textarea value={formQ.adresseBailleur}
                      onChange={e => setFormQ(f => ({ ...f, adresseBailleur: e.target.value }))}
                      placeholder="Ex: 12 rue des Lilas, 62000 Arras"
                      rows={2}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none resize-none" />
                  </div>

                  {/* Récapitulatif total compact */}
                  <div className="bg-blue-50 rounded-lg px-3 py-2 flex items-center justify-between">
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <p>Loyer HC : <span className="font-medium text-gray-700">{fmt(formQ.loyerHC)}</span></p>
                      <p>Charges : <span className="font-medium text-gray-700">{fmt(formQ.charges)}</span></p>
                      {formQ.regularisationMontant && (
                        <p>{formQ.regularisationLibelle || 'Régul.'} : <span className="font-medium text-gray-700">{fmt(parseFloat(formQ.regularisationMontant.replace(',', '.')) || 0)}</span></p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-lg font-bold text-blue-700">
                        {fmt(formQ.loyerHC + formQ.charges + (parseFloat(formQ.regularisationMontant.replace(',', '.')) || 0))}
                      </p>
                    </div>
                  </div>
                </div>

                {savingError && <p className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded-lg">{savingError}</p>}

                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => setShowModalQuittance(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                    Annuler
                  </button>
                  <button
                    onClick={handleGenererQuittance}
                    disabled={saving || !formQ.mois || !formQ.datePaiement || !formQ.adresseBailleur}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Générer le PDF
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        window.document.body
      )}

      {/* ============================================ */}
      {/* MODALE ARCHIVAGE                            */}
      {/* ============================================ */}

      {showModalArchive && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModalArchive(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
                <Archive className="w-4 h-4 text-amber-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Archiver ce locataire ?</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {locataire.prenom} {locataire.nom} sera marqué comme ancien locataire. Les données et l'historique des paiements sont conservés.
            </p>
            {savingError && <p className="mb-3 text-sm text-red-600 bg-red-50 p-2 rounded-lg">{savingError}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModalArchive(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Annuler
              </button>
              <button onClick={handleArchiver} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                Archiver
              </button>
            </div>
          </div>
        </div>,
        window.document.body
      )}

    </div>
  );
}
